import React, { useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import BaseModal from "../../../components/ui/BaseModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { createCategory } from "../../../services/api/category";
import { showToast } from "../../../store/toast/toastSlice";

const validationSchema = yup.object({
  name: yup.string().trim().required("Category is required"),
});

export default function AddEditCategoryModal({
  open,
  close,
  mode = "add",
  initialData = null,
  refetchCategories,
}) {
  const isEdit = mode === "edit";
  const dispatch = useDispatch();
  const {
    isPending: isCreateLoading,
    error: createError,
    data: createResp,
    mutateAsync: createCategoryMutation,
  } = useMutation({
    mutationFn: createCategory,
  });

  const handleCreateCategory = async () => {
    try {
      await createCategoryMutation({ category: formik?.values?.name });
      dispatch(showToast({ message: "Technology stack created", type: "success" }));
      refetchCategories();
      formik.resetForm();
      close(false);
    } catch (err) {}
  };

  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema,
    onSubmit: handleCreateCategory,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (open) formik.setValues({ name: initialData?.name ?? "" }, false);
  }, [open, initialData]);

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={close}
      >
        <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
          <h6 className="text-dark font-normal border-b pb-1">
            {isEdit ? "Edit Technology Stack" : "Create New Technology Stack"}
          </h6>

          <div className="grid grid-cols-2 gap-x-8 mt-8">
            <div className="col-span-2">
              <p className="font-semibold mb-2">Technology stack name</p>
              <BaseTextField
                id="name"
                name="name"
                placeholder="Networking"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            onClick={formik.handleSubmit}
            disabled={!formik.values?.name?.trim()}
          >
            {isEdit ? "Save" : "Create"}
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
}
